import { UserPermission } from '@globaleris/ui.library'
import { Button, Card, CardContent } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import {
  SettlementGroupPatchRequest,
  SettlementGroupTable
} from '../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { EnumTypeSettlementGroup } from '../../api/Argentia/enums/typeSettlementGroup'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { getUserPermissions, hasPermission } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../model'
import { Pages, Resources } from '../../model/RBAC/constants'
import { storeActiveTabForCurrentPage } from '../../redux'
import { SettlementGroupsTable } from './components/settlementGroupsTable'
import {
  useSettlementGroupsBusinessGroup,
  useSettlementGroupsVirtualBattery,
  useSettlementGroupsWithContracts,
  useSettlementGroupsWithFlexibilities
} from './settlementGroups.hooks'
import { mapSettlementGroupTableToVirtualDetail } from './settlementGroups.mapper'
import { TabsSettlementGroup } from './settlementGroups.model'

interface SettlementGroupsProps {
  initialGroupCode: string
}

export const SettlementGroupsPage: React.FC<SettlementGroupsProps> = ({
  initialGroupCode = undefined
}) => {
  const history = useHistory()
  const [t] = useTranslation(['settlementGroups', 'globalerisLibrary', 'enums'])
  const { dataTableFilter, dataTableSearch, activeTab } = usePageState()
  const [showFlexibilities, setShowFlexibilities] = useState<string | undefined>(initialGroupCode)
  const [currentTab, setCurrentTab] = useState<number>(activeTab ?? 0)

  const {
    settlementGroupsWithFlexibilities,
    fetchSettlementGroupsWithFlexibilities,
    isFetching: isFetchingSettlementGroupsWithFlexibilities,
    onAdd,
    onAddLoading,
    onEdit,
    onEditLoading,
    onDelete,
    onRefresh: onRefreshSettlementGroupsWithFlexibilities
  } = useSettlementGroupsWithFlexibilities()

  const {
    settlementGroupsWithContracts,
    fetchSettlementGroupsWithContracts,
    isFetching: isFetchingSettlementGroupsWithContracts,
    onRefresh: onRefreshSettlementGroupsWithContracts
  } = useSettlementGroupsWithContracts()

  const {
    settlementGroupsBusinessGroup,
    fetchSettlementGroupsBusinessGroup,
    isFetching: isFetchingSettlementGroupsBusinessGroup,
    onRefresh: onRefreshSettlementGroupsBusinessGroup
  } = useSettlementGroupsBusinessGroup()

  const {
    settlementGroupsVirtualBattery,
    fetchSettlementGroupsVirtualBattery,
    isFetching: isFetchingSettlementGroupsVirtualBattery,
    onRefresh: onRefreshSettlementGroupsVirtualBattery
  } = useSettlementGroupsVirtualBattery()

  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )

  //#region Tab Region Starts

  const tabsDefinition: Map<any, Tab> = new Map([
    [
      TabsSettlementGroup.groupFlexibilities,
      {
        id: TabsSettlementGroup.groupFlexibilities,
        label: t('settlementGroups:tabs.groupFlexibilities'),
        hasPermission: hasPermission(Resources.Flexibility, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ],
    [
      TabsSettlementGroup.AggregateAndSummaryInvoice,
      {
        id: TabsSettlementGroup.AggregateAndSummaryInvoice,
        label: t('settlementGroups:tabs.AggregateAndSummaryInvoice'),
        hasPermission: hasPermission(Resources.GroupedBilling, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ],
    [
      TabsSettlementGroup.virtualBattery,
      {
        id: TabsSettlementGroup.virtualBattery,
        label: t('settlementGroups:tabs.virtualBattery'),
        hasPermission: hasPermission(Resources.VirtualBattery, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ],
    [
      TabsSettlementGroup.BusinessGroup,
      {
        id: TabsSettlementGroup.BusinessGroup,
        label: t('settlementGroups:tabs.businessGroup'),
        hasPermission: hasPermission(Resources.Flexibility, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ]
  ])

  // filter tabs with permissions to avoid getting hidden tabs values onTabChange
  const tabsArray = Array.from(tabsDefinition.values()).filter(tab => tab.hasPermission)

  //#endregion Tab Region Starts

  //#region Handlers Region Starts

  const handleShowChildSettlementGroups = (group: SettlementGroupTable) => {
    history.push(NonAuthRoutes.SettlementGroupChild, {
      settlementGroupId: group.id,
      settlementGroupCode: group.code,
      settlementGroupDesc: group.description
    })
  }

  const handleShowFlexibilities = (group: SettlementGroupTable) => {
    history.push(NonAuthRoutes.SettlementGroupFlexibilities, {
      settlementGroupId: group.id,
      settlementGroupCode: group.code
    })
  }

  const handleShowContracts = (group: SettlementGroupTable, flag: EnumTypeSettlementGroup) => {
    history.push(NonAuthRoutes.SettlementGroupContracts, {
      settlementGroupId: group.id,
      settlementGroupCode: group.code,
      settlementGroupSubject: group.subject,
      flag: flag
    })
  }

  const handleShowSubjects = (group: SettlementGroupTable, flag: EnumTypeSettlementGroup) => {
    history.push(NonAuthRoutes.SettlementGroupSubjects, {
      settlementGroupId: group.id,
      settlementGroupCode: group.code,
      flag: flag,
      subjectList: group.subjectList
    })
  }
  const handleSettlementGroupAggregatedAdd = () => {
    history.push(NonAuthRoutes.SettlementGroupAggregatedAdd, {
      settlementGroupCodes: settlementGroupsWithContracts.map(({ id, code }) => ({ id, code }))
    })
    return Promise.resolve(true) // only for typescript compatibility Reasons
  }

  const handleSettlementGroupAggregatedEdit = (
    row: SettlementGroupTable | SettlementGroupPatchRequest
  ) => {
    history.push(NonAuthRoutes.SettlementGroupAggregatedEdit, {
      settlementGroup: row,
      settlementGroupCodes: settlementGroupsWithContracts.map(({ id, code }) => ({ id, code }))
    })
    return Promise.resolve(true) // only for typescript compatibility Reasons
  }

  const handleViewDetails = (group: SettlementGroupTable) => {
    history.push(NonAuthRoutes.virtualBatteryDetail, mapSettlementGroupTableToVirtualDetail(group))
  }

  const onTabChangeHandler = (tab: number) => {
    if (currentTab !== tab) {
      setCurrentTab(tab)
      storeActiveTabForCurrentPage(tab)
    }
  }
  //#endregion Handlers Region Starts

  // #region Functions Region Starts

  const renderTabContent = (activeTab: number) => {
    switch (tabsArray[activeTab].id) {
      case TabsSettlementGroup.groupFlexibilities: {
        return (
          <Card raised>
            <CardContent>
              <SettlementGroupsTable
                activeTab={tabsArray[activeTab].id}
                userPermissions={getUserPermissions(Pages.Flexibility)}
                settlementGroups={settlementGroupsWithFlexibilities}
                isFetchingSettlementGroups={isFetchingSettlementGroupsWithFlexibilities}
                refreshSettlementGroups={onRefreshSettlementGroupsWithFlexibilities}
                onAdd={onAdd}
                onAddLoading={onAddLoading}
                onEdit={onEdit}
                onEditLoading={onEditLoading}
                onDelete={onDelete}
                onViewFlexibilities={handleShowFlexibilities}
                onViewContracts={handleShowContracts}
                onViewSubjects={handleShowSubjects}
                onViewChildSettlementGroups={handleShowChildSettlementGroups}
                dataTableFilter={dataTableFilter}
                dataTableSearch={dataTableSearch}
              />
            </CardContent>
          </Card>
        )
      }
      case TabsSettlementGroup.AggregateAndSummaryInvoice: {
        return (
          <Card raised>
            <CardContent>
              <SettlementGroupsTable
                activeTab={tabsArray[activeTab].id}
                userPermissions={getUserPermissions(Pages.SettlementGroupAggregatedAdd)}
                settlementGroups={settlementGroupsWithContracts}
                isFetchingSettlementGroups={isFetchingSettlementGroupsWithContracts}
                refreshSettlementGroups={onRefreshSettlementGroupsWithContracts}
                onAdd={handleSettlementGroupAggregatedAdd}
                onAddLoading={onAddLoading}
                onEdit={handleSettlementGroupAggregatedEdit}
                onEditLoading={onEditLoading}
                onDelete={onDelete}
                onViewContracts={handleShowContracts}
                dataTableFilter={dataTableFilter}
                dataTableSearch={dataTableSearch}
              />
            </CardContent>
          </Card>
        )
      }
      case TabsSettlementGroup.virtualBattery: {
        return (
          <Card raised>
            <CardContent>
              <SettlementGroupsTable
                activeTab={tabsArray[activeTab].id}
                userPermissions={getUserPermissions(Pages.VirtualBatteryDetail)}
                settlementGroups={settlementGroupsVirtualBattery}
                isFetchingSettlementGroups={isFetchingSettlementGroupsVirtualBattery}
                refreshSettlementGroups={onRefreshSettlementGroupsVirtualBattery}
                onAdd={onAdd}
                onAddLoading={onAddLoading}
                onEdit={onEdit}
                onEditLoading={onEditLoading}
                onDelete={onDelete}
                onViewContracts={handleShowContracts}
                onViewDetails={handleViewDetails}
                dataTableFilter={dataTableFilter}
                dataTableSearch={dataTableSearch}
              />
            </CardContent>
          </Card>
        )
      }
      case TabsSettlementGroup.BusinessGroup: {
        return (
          <Card raised>
            <CardContent>
              <SettlementGroupsTable
                activeTab={tabsArray[activeTab].id}
                userPermissions={[UserPermission.EDIT]}
                settlementGroups={settlementGroupsBusinessGroup}
                isFetchingSettlementGroups={isFetchingSettlementGroupsBusinessGroup}
                refreshSettlementGroups={onRefreshSettlementGroupsBusinessGroup}
                onAdd={onAdd}
                onAddLoading={onAddLoading}
                onEdit={onEdit}
                onEditLoading={onEditLoading}
                onDelete={onDelete}
                onViewContracts={handleShowContracts}
                onViewSubjects={handleShowSubjects}
                onViewChildSettlementGroups={handleShowChildSettlementGroups}
                dataTableFilter={dataTableFilter}
                dataTableSearch={dataTableSearch}
              />
            </CardContent>
          </Card>
        )
      }
      default:
        return null
    }
  }

  // #endregion Functions Region Ends

  // #region Hooks Region Starts

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  useEffect(() => {
    switch (tabsArray[currentTab].id) {
      case TabsSettlementGroup.groupFlexibilities:
        fetchSettlementGroupsWithFlexibilities()
        break
      case TabsSettlementGroup.AggregateAndSummaryInvoice:
        fetchSettlementGroupsWithContracts()
        break
      case TabsSettlementGroup.virtualBattery:
        fetchSettlementGroupsVirtualBattery()
        break
      case TabsSettlementGroup.BusinessGroup:
        fetchSettlementGroupsBusinessGroup()
        break
      default:
        break
    }
  }, [currentTab])
  // #endregion Hooks Region Ends

  return (
    <Page notification={globalNotification}>
      <PageHeader title={t('settlementGroups:title')}>
        {showFlexibilities && (
          <Button color="primary" onClick={() => setShowFlexibilities(undefined)}>
            {t('settlementGroups:actions.back')}
          </Button>
        )}
      </PageHeader>
      <TabsWithPermissions
        tabs={tabsDefinition}
        tabDefault={currentTab}
        onTabChange={onTabChangeHandler}
      ></TabsWithPermissions>
      {renderTabContent(activeTab ? activeTab : currentTab)}
    </Page>
  )
}
